export const DEFAULT_POLICY = {
  profile: "standard",
  minimumOverallScore: 75,
  minimumNamingScore: 70,
  minimumConsentCoverage: 80,
  maximumPausedTags: 5,
  allowCustomHtml: true,
  requireGa4EventTags: true,
};

const SEVERITY_WEIGHT = { error: 100, warning: 55, info: 20 };

export function buildGovernanceInsights(container, analysis, policy = DEFAULT_POLICY) {
  if (!container || !analysis) return null;
  const tags = container.tags || [];
  const triggerIds = new Set((container.triggers || []).map((item) => String(item.triggerId)));
  const consentCount = tags.filter(hasExplicitConsent).length;
  const triggeredCount = tags.filter((tag) =>
    asArray(tag.firingTriggerId).some(
      (id) => triggerIds.has(String(id)) || isReservedTriggerId(id),
    ),
  ).length;
  const customHtmlCount = tags.filter((tag) => String(tag.type).toLowerCase() === "html").length;
  const eventFindings = analysis.findings.filter((item) => item.tabId === "events");
  const coverage = {
    consent: percent(consentCount, tags.length),
    triggerIntegrity: percent(triggeredCount, tags.length),
    naming: analysis.ratings.naming.score ?? 0,
    measurement: analysis.ratings.measurement.score,
    activeTags: percent(analysis.counts.activeTags, tags.length),
  };
  const controls = [
    control(
      "Overall audit threshold",
      analysis.ratings.overall.score >= policy.minimumOverallScore,
      `${analysis.ratings.overall.score}/100`,
      `Minimum ${policy.minimumOverallScore}/100`,
    ),
    control(
      "Naming governance",
      analysis.ratings.naming.score >= policy.minimumNamingScore,
      `${analysis.ratings.naming.score}/100`,
      `Minimum ${policy.minimumNamingScore}/100`,
    ),
    control(
      "Consent coverage",
      coverage.consent >= policy.minimumConsentCoverage,
      `${coverage.consent}%`,
      `Minimum ${policy.minimumConsentCoverage}%`,
    ),
    control(
      "Paused tag budget",
      analysis.counts.pausedTags <= policy.maximumPausedTags,
      String(analysis.counts.pausedTags),
      `Maximum ${policy.maximumPausedTags}`,
    ),
    control(
      "Custom HTML policy",
      policy.allowCustomHtml || customHtmlCount === 0,
      String(customHtmlCount),
      policy.allowCustomHtml ? "Allowed, review manually" : "Must be zero",
    ),
    control(
      "GA4 event implementation",
      !policy.requireGa4EventTags || analysis.counts.ga4Events > 0,
      String(analysis.counts.ga4Events),
      policy.requireGa4EventTags ? "At least one required" : "Optional",
    ),
  ];
  const passed = controls.filter((item) => item.passed).length;
  const score = Math.round((passed / controls.length) * 100);
  const riskRegister = buildRiskRegister(analysis.findings);
  const graph = buildDependencySummary(container);

  return {
    profile: policy.profile,
    score,
    grade: gradeForScore(score),
    passed,
    failed: controls.length - passed,
    total: controls.length,
    controls,
    coverage,
    customHtmlCount,
    eventFindingCount: eventFindings.length,
    riskRegister,
    graph,
  };
}

export function compareContainers(current, baseline, currentAnalysis, baselineAnalysis) {
  if (!current || !baseline || !currentAnalysis || !baselineAnalysis) return null;
  const objectTypes = [
    ["tags", "tagId", "Tag"],
    ["triggers", "triggerId", "Trigger"],
    ["variables", "variableId", "Variable"],
  ];
  const changes = [];
  const summary = {};

  objectTypes.forEach(([collection, idKey, label]) => {
    const before = new Map(
      baseline[collection].map((item, index) => [stableId(item, idKey, index), item]),
    );
    const after = new Map(
      current[collection].map((item, index) => [stableId(item, idKey, index), item]),
    );
    let added = 0;
    let removed = 0;
    let modified = 0;

    after.forEach((item, id) => {
      const previous = before.get(id);
      if (!previous) {
        added += 1;
        changes.push(change(label, "added", item.name, "", item.type));
        return;
      }
      if (
        String(previous.name || "") !== String(item.name || "") ||
        String(previous.type || "") !== String(item.type || "") ||
        Boolean(previous.paused) !== Boolean(item.paused)
      ) {
        modified += 1;
        changes.push(
          change(
            label,
            "modified",
            item.name,
            previous.name,
            describeModification(previous, item),
          ),
        );
      }
    });
    before.forEach((item, id) => {
      if (!after.has(id)) {
        removed += 1;
        changes.push(change(label, "removed", "", item.name, item.type));
      }
    });
    summary[collection] = {
      before: baseline[collection].length,
      after: current[collection].length,
      delta: current[collection].length - baseline[collection].length,
      added,
      removed,
      modified,
    };
  });

  const scoreDelta = {
    overall: scoreDifference(currentAnalysis.ratings.overall, baselineAnalysis.ratings.overall),
    health: scoreDifference(currentAnalysis.ratings.health, baselineAnalysis.ratings.health),
    naming: scoreDifference(currentAnalysis.ratings.naming, baselineAnalysis.ratings.naming),
    measurement: scoreDifference(
      currentAnalysis.ratings.measurement,
      baselineAnalysis.ratings.measurement,
    ),
  };

  return {
    summary,
    scoreDelta,
    changes: changes.sort((left, right) => {
      const order = { removed: 0, modified: 1, added: 2 };
      return order[left.change] - order[right.change] || left.objectType.localeCompare(right.objectType);
    }),
    changeCount: changes.length,
    regression:
      scoreDelta.overall !== null && scoreDelta.overall < 0
        ? Math.abs(scoreDelta.overall)
        : 0,
  };
}

export function createAuditSnapshot({
  container,
  analysis,
  sourceName,
  governance,
}) {
  return {
    sourceName,
    containerName: container?.metadata?.name || "Unnamed container",
    ratings: analysis.ratings,
    counts: analysis.counts,
    severityCounts: analysis.ratings.overall.counts,
    findingCount: analysis.findings.length,
    topFindings: analysis.findings.slice(0, 30).map((item) => ({
      severity: item.severity,
      category: item.category,
      item: item.item,
      description: item.description,
      tabId: item.tabId,
    })),
    policy: governance
      ? {
          score: governance.score,
          passed: governance.passed,
          total: governance.total,
        }
      : null,
  };
}

function buildRiskRegister(findings) {
  const grouped = new Map();
  findings.forEach((item) => {
    const key = `${item.tabId}:${item.category}`;
    const group = grouped.get(key) || {
      category: item.category,
      section: item.tabId,
      severity: item.severity,
      count: 0,
      examples: [],
    };
    group.count += 1;
    if (SEVERITY_WEIGHT[item.severity] > SEVERITY_WEIGHT[group.severity]) {
      group.severity = item.severity;
    }
    if (group.examples.length < 3) group.examples.push(item.item);
    grouped.set(key, group);
  });

  return [...grouped.values()]
    .map((group) => ({
      ...group,
      priority: priorityFor(group.severity, group.count),
      owner: ownerFor(group.section, group.category),
      effort: effortFor(group.category, group.count),
      riskScore: Math.min(
        100,
        SEVERITY_WEIGHT[group.severity] + Math.min(20, (group.count - 1) * 4),
      ),
      recommendation: recommendationFor(group.category),
    }))
    .sort((left, right) => right.riskScore - left.riskScore)
    .slice(0, 30);
}

function buildDependencySummary(container) {
  const triggerIds = new Set(container.triggers.map((item) => String(item.triggerId)));
  const referencedTriggers = new Set();
  const variableNames = new Set(container.variables.map((item) => item.name).filter(Boolean));
  const referencedVariables = new Set();
  let tagTriggerEdges = 0;

  container.tags.forEach((tag) => {
    [...asArray(tag.firingTriggerId), ...asArray(tag.blockingTriggerId)].forEach((id) => {
      const normalized = String(id);
      if (triggerIds.has(normalized) || isReservedTriggerId(normalized)) {
        referencedTriggers.add(normalized);
        tagTriggerEdges += 1;
      }
    });
  });
  [container.tags, container.triggers, container.variables].forEach((collection) => {
    collection.forEach((item) => {
      collectVariableReferences(item, referencedVariables);
    });
  });
  const usedVariables = [...referencedVariables].filter((name) => variableNames.has(name)).length;

  return {
    nodes: container.tags.length + container.triggers.length + container.variables.length,
    tagTriggerEdges,
    referencedTriggers: [...referencedTriggers].filter((id) => triggerIds.has(id)).length,
    usedVariables,
    variableUtilization: percent(usedVariables, container.variables.length),
    triggerUtilization: percent(
      [...referencedTriggers].filter((id) => triggerIds.has(id)).length,
      container.triggers.length,
    ),
  };
}

function hasExplicitConsent(tag) {
  const settings = tag?.consentSettings;
  if (!settings || typeof settings !== "object") return false;
  if (settings.consentStatus === "notNeeded") return true;
  return (
    settings.consentStatus === "needed" &&
    Array.isArray(settings.consentType?.list) &&
    settings.consentType.list.length > 0
  );
}

function collectVariableReferences(value, target) {
  if (typeof value === "string") {
    const regex = /{{\s*([^{}]+?)\s*}}/g;
    let match;
    while ((match = regex.exec(value))) target.add(match[1].trim());
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((item) => collectVariableReferences(item, target));
    return;
  }
  if (value && typeof value === "object") {
    Object.values(value).forEach((item) => collectVariableReferences(item, target));
  }
}

function priorityFor(severity, count) {
  if (severity === "error" || (severity === "warning" && count >= 5)) return "P1";
  if (severity === "warning" || count >= 8) return "P2";
  return "P3";
}

function ownerFor(section, category) {
  if (/consent|custom html/i.test(category)) return "Privacy / Engineering";
  if (section === "events") return "Analytics implementation";
  if (section === "naming") return "Analytics governance";
  return "GTM owner";
}

function effortFor(category, count) {
  if (/duplicate|orphaned|paused|prefix|separator|casing/i.test(category)) {
    return count <= 3 ? "Low" : "Medium";
  }
  if (/consent|dangling|trigger|parameter/i.test(category)) return "Medium";
  return count > 6 ? "High" : "Medium";
}

function recommendationFor(category) {
  const recommendations = [
    [/consent/i, "Document consent requirements and configure explicit consent settings before publishing."],
    [/orphaned/i, "Confirm ownership, then remove unused objects or reconnect valid dependencies."],
    [/dangling/i, "Replace missing references with valid triggers and regression-test the firing path."],
    [/paused/i, "Archive obsolete tags and document intentionally paused implementations."],
    [/duplicate/i, "Consolidate duplicate objects and update all references to one governed implementation."],
    [/prefix|separator|casing|name/i, "Rename to the approved convention and update the governance dictionary."],
    [/parameter|items|currency|event/i, "Correct the payload against the GA4 schema and validate with a test purchase or event."],
  ];
  return recommendations.find(([pattern]) => pattern.test(category))?.[1] ||
    "Review the affected objects, document the decision, and regression-test before publishing.";
}

function stableId(item, key, index) {
  const id = item?.[key];
  return id === undefined || id === null || id === ""
    ? `name:${String(item?.name || "")}:${index}`
    : `id:${String(id)}`;
}

function describeModification(before, after) {
  const changes = [];
  if (String(before.name || "") !== String(after.name || "")) changes.push("name");
  if (String(before.type || "") !== String(after.type || "")) changes.push("type");
  if (Boolean(before.paused) !== Boolean(after.paused)) changes.push("status");
  return `${changes.join(", ")} changed`;
}

function change(objectType, changeType, currentName, baselineName, detail) {
  return {
    objectType,
    change: changeType,
    currentName: currentName || "—",
    baselineName: baselineName || "—",
    detail: String(detail || "—"),
  };
}

function scoreDifference(current, baseline) {
  if (current?.score === null || baseline?.score === null) return null;
  if (!Number.isFinite(current?.score) || !Number.isFinite(baseline?.score)) return null;
  return current.score - baseline.score;
}

function control(name, passed, actual, target) {
  return { name, passed, actual, target };
}

function percent(numerator, denominator) {
  return denominator ? Math.round((numerator / denominator) * 100) : 100;
}

function gradeForScore(score) {
  if (score >= 90) return "A";
  if (score >= 80) return "B";
  if (score >= 70) return "C";
  if (score >= 60) return "D";
  return "F";
}

function isReservedTriggerId(value) {
  return ["2147479552", "2147479553", "2147479554"].includes(String(value));
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}
